import type { FC } from 'react';

import { useCms, useContent, useSetting } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import RichTextComponent from '@eneco/components/RichText/RichText';
import { Checklist } from '@eneco/components/ui/Checklist/Checklist';
import { Box, ButtonLink, Grid, Heading, Stack, Stretch, Text, TextLink } from '@eneco/sparky';
import type { HeadingProps } from '@eneco/sparky/types';

import type { ListContent } from './list-content.types';

/**
 * `List` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy Sitecore lane (`libs/sitecore/components/content/src/List/List.tsx`): an optional
 * title, a checklist of items, an optional primary button and an optional secondary text link.
 *
 * Known simplification: `items` renders no entries on the Sanity lane today. The CMS schema's
 * `List.items` (`node_modules/@eneco/cms-sanity-schema/dist/sections/List.js`) is an array of references to
 * `migrationUnresolvedReference` (ADR-0049) — a placeholder with only `label`/`note`, not the item's own
 * `title`; real per-item content is deferred to ADR-0044, exactly like `Accordion.items`. See
 * `list-content.types.ts` for the full explanation.
 */
const List: FC = () => {
  const { adapter } = useCms();
  const content = useContent<ListContent>();

  const headingLevel = (useSetting('headingLevel') as HeadingProps['as']) ?? 'h3';

  const buttonLink = linkWithUrl(content.buttonLink);
  const link = linkWithUrl(content.link);
  const items = (content.items ?? []).filter(item => item.title);

  return (
    <Box paddingX={{ initial: '6', md: '16', lg: '24' }} paddingY={{ initial: '10', md: '24' }}>
      <Stack alignY="center">
        <Grid.Item>
          {content.title && (
            <Box paddingBottom="6">
              <Heading as={headingLevel} size="S" color="inherit">
                {content.title}
              </Heading>
            </Box>
          )}
          {items.length > 0 && (
            <Checklist gap="5">
              {items.map((item, key) => (
                <Checklist.Item key={item.id ?? key} icon="checkmark-filled" gap="3">
                  <Text size={{ initial: 'BodyM', md: 'BodyL' }} weight="bold">
                    <ListItemTitle title={item.title} toRichText={adapter.toRichText} />
                  </Text>
                </Checklist.Item>
              ))}
            </Checklist>
          )}
          {buttonLink?.href && buttonLink.text && (
            <Box paddingTop={{ initial: '6' }}>
              <Stretch width={{ initial: true, md: false }}>
                <ButtonLink href={buttonLink.href} target={buttonLink.target}>
                  {buttonLink.text}
                </ButtonLink>
              </Stretch>
            </Box>
          )}
          {link?.href && link.text && (
            <Box paddingTop={{ initial: '6' }}>
              <Stretch width={{ initial: true, md: false }}>
                <TextLink href={link.href} target={link.target} emphasis="high">
                  {link.text}
                </TextLink>
              </Stretch>
            </Box>
          )}
        </Grid.Item>
      </Stack>
    </Box>
  );
};

const ListItemTitle: FC<{ title: unknown; toRichText: (raw: unknown) => string | undefined }> = ({
  title,
  toRichText,
}) => {
  const html = toRichText(title);
  if (html) return <RichTextComponent html={html} />;
  return typeof title === 'string' ? title : null;
};

export default List;
